import { Icon } from '@dx-ui/osc-icon';
import { Popover } from 'radix-ui';
import cx from 'classnames';
import { sendReward } from '@dx-ui/framework-conductrics';
import { Trans, useTranslation } from 'next-i18next';
import { useContext, useEffect } from 'react';
import { SpeechBubbleContext, trackNavClick } from '../header.utilities';
import { Button, Surface, Text } from '@dx-ui/global-ui-prism';
import { useIsClient, useMediaQuery } from 'usehooks-ts';

type SpeechBubbleProps = {
  showSpeechBubble: boolean;
  closeSpeechBubble: () => void;
  onInlineButtonClick: () => void;
  containerRef?: Element | null;
  contentStyles?: string;
};
//The internal radix's logic traps focus inside the popover. This is a workaround
const getDisplayedFocusableElements = () => {
  const body = document.querySelector('body');

  if (body) {
    const focusableElements = body.querySelectorAll(
      'a, button, input, textarea, select, details, summary, [tabindex]:not([tabindex="-1"]), iframe, area'
    );

    const displayedFocusableElements = Array.from(focusableElements).filter((element) => {
      const style = window.getComputedStyle(element);
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        (element as HTMLElement).offsetWidth > 0 &&
        (element as HTMLElement).offsetHeight > 0
      );
    });

    return displayedFocusableElements as HTMLElement[];
  }
};

export const SpeechBubble: React.FC<React.PropsWithChildren<SpeechBubbleProps>> = ({
  closeSpeechBubble,
  showSpeechBubble,
  contentStyles,
  children,
  containerRef,
  onInlineButtonClick,
}) => {
  const { t } = useTranslation(['osc-header']);

  const isSmallView = useMediaQuery('(max-width: 768px)');
  const isClient = useIsClient();

  const { isVariantB6266MVT, isBookSpeechBubble, shouldShowOnMobile } =
    useContext(SpeechBubbleContext);

  const onCloseSpeechBubble = () => {
    trackNavClick('bubble:close');
    closeSpeechBubble();
  };

  const onSpeechBubbleButtonClick = () => {
    trackNavClick('bubble:signin');
    sendReward('g-JtxVq7s7u9');
    onInlineButtonClick?.();
  };
  //The internal radix's logic traps focus inside the popover. This is a workaround
  const onKeyDownCloseButton = (e: React.KeyboardEvent) => {
    const displayedFocusableElements = getDisplayedFocusableElements();

    if (e.shiftKey && e.key === 'Tab' && displayedFocusableElements) {
      e.preventDefault();

      //of the currently focused element
      const currentIndex = Array.prototype.indexOf.call(
        displayedFocusableElements,
        document.activeElement
      );
      //in the list of displayed focusable items
      const previousIndex =
        (currentIndex - 1 + displayedFocusableElements.length) % displayedFocusableElements.length;

      displayedFocusableElements[previousIndex]?.focus();
    }
  };
  //The internal radix's logic traps focus inside the popover. This is a workaround
  const onKeyDownInlineButton = (e: React.KeyboardEvent) => {
    const displayedFocusableElements = getDisplayedFocusableElements();

    if (!e.shiftKey && e.key === 'Tab' && displayedFocusableElements) {
      e.preventDefault();
      //of the currently focused element
      const currentIndex = Array.prototype.indexOf.call(
        displayedFocusableElements,
        document.activeElement
      );
      //in the list of displayed focusable items
      const nextIndex = (currentIndex + 1) % displayedFocusableElements.length;

      displayedFocusableElements[nextIndex]?.focus();
    }
  };

  //This is another workaround to remove the focus guards that radix injects under the hood
  //This is not a good long term solution, and we might need to rethink using radix for a speech bubble
  useEffect(() => {
    if (showSpeechBubble) {
      //remove existing focus guards
      const focusGuards = document.querySelectorAll('span[data-radix-focus-guard]');
      focusGuards.forEach((el) => el.remove());

      // Select the parent node where the focus guards are added
      const parentNode = document.body;

      const config: MutationObserverInit = { childList: true, subtree: false };

      const callback: MutationCallback = (mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (
                node.nodeType === 1 &&
                (node as HTMLElement).hasAttribute('data-radix-focus-guard')
              ) {
                (node as HTMLElement).remove();
              }
            });
          }
        }
      };
      const observer = new MutationObserver(callback);
      observer.observe(parentNode, config);

      return () => {
        observer.disconnect();
      };
    }
  }, [showSpeechBubble]);

  if (!isClient) return children;
  if (isSmallView && !shouldShowOnMobile && !isBookSpeechBubble) return children;

  return (
    <div>
      <Popover.Root open={showSpeechBubble}>
        <Popover.Trigger asChild>{children}</Popover.Trigger>
        <Popover.Portal container={containerRef}>
          <Surface type="contrast">
            <Popover.Content
              className={cx(
                'z-10 flex w-[80vw] max-w-[21rem] flex-col rounded-md bg-points shadow-lg',
                contentStyles
              )}
              align="end"
              onEscapeKeyDown={onCloseSpeechBubble}
              aria-label={t('speechBubble.signInMessage')}
              onOpenAutoFocus={(event) => event.preventDefault()}
            >
              <div className="flex justify-end">
                <Popover.Close
                  aria-label={t('speechBubble.closeSignInMessage')}
                  onClick={onCloseSpeechBubble}
                  onKeyDown={onKeyDownCloseButton}
                >
                  <Icon name="close" size="md" />
                </Popover.Close>
              </div>
              <Text className="px-4 pb-4" id="sign-in-note" size="sm">
                {isVariantB6266MVT ? (
                  <>
                    <span className="mb-2 block">Unlock exclusive benefits with Hilton Honors</span>
                    <Button
                      label="Join or sign in"
                      fill="outline"
                      size="sm"
                      onClick={onSpeechBubbleButtonClick}
                      onKeyDown={onKeyDownInlineButton}
                    />
                  </>
                ) : (
                  <Trans
                    t={t}
                    i18nKey={`speechBubble.${isBookSpeechBubble ? 'bookSignInMessage' : 'joinOrSignInMessage'}`}
                    components={[
                      <button
                        onClick={onSpeechBubbleButtonClick}
                        onKeyDown={onKeyDownInlineButton}
                        type="button"
                        className="underline"
                        key="join-button"
                      />,
                    ]}
                  />
                )}
              </Text>
              <Popover.Arrow
                className="fill-points lg:ml-[36px]"
                width={15}
                height={10}
                aria-hidden={true}
              />
            </Popover.Content>
          </Surface>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

export default SpeechBubble;
